"use client";

import createClient, { type Middleware } from "openapi-fetch";

import { URL_BACKEND } from "../lib/constants/urls/urlBackend";
import { requestSessionRefresh } from "../session/utils/requests/requestSessionRefresh";
import type { paths } from "./generated/schema";
import type { GetRequestHeadersProps } from "./utils/getRequestHeaders";
import { getRequestHeaders } from "./utils/getRequestHeaders";

export type CreateOpenApiClientProps = {
  headersValues?: GetRequestHeadersProps["values"];
  onTokenRefresh?: (token: string) => void;
};

export const createOpenApiClient = ({
  headersValues = {},
  onTokenRefresh,
}: CreateOpenApiClientProps = {}) => {
  const client = createClient<paths>({
    baseUrl: URL_BACKEND,
    credentials: "include",
  });

  const requestCache: Map<Request, Request> = new Map<Request, Request>();

  // Middleware to add auth headers
  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const headers = await getRequestHeaders({
        requestType: "authenticated",
        values: headersValues,
      });

      /* cache the current token so we're not constantly hitting /oauth/refresh because this was
       * initialized without a token */
      if (headers["Authorization"])
        headersValues.token = headers["Authorization"].split("Bearer ")[1];

      Object.entries(headers).forEach(([key, value]) => {
        if (value) request.headers.set(key, value);
      });

      requestCache.set(request, request.clone());

      return request;
    },
    async onResponse({ response, request }) {
      // Handle token refresh from response header
      if (response.headers.has("x-refreshed-token") && onTokenRefresh) {
        try {
          const newToken = JSON.parse(
            response.headers.get("x-refreshed-token") ?? "",
          ).access_token;
          if (newToken) {
            onTokenRefresh(newToken);
          }
        } catch {
          // Ignore parse errors
        }
      }

      if (!response.ok && response.status === 401) {
        if (response.url.endsWith("/oauth/refresh")) return response;
        const originalRequest = requestCache.get(request);
        requestCache.delete(request);
        if (!originalRequest) return response;
        const { access_token } = await requestSessionRefresh();
        onTokenRefresh?.(access_token);
        const newHeaders = new Headers(originalRequest.headers);
        newHeaders.set("Authorization", `Bearer ${access_token}`);

        const newRequest = new Request(originalRequest, {
          headers: newHeaders,
        });

        return fetch(newRequest);
      }

      requestCache.delete(request);
      return response;
    },
    onError({ request }) {
      requestCache.delete(request);
    },
  };

  client.use(authMiddleware);

  return client;
};
